import { useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AcademicCapIcon, ArrowLeftIcon, ChartBarIcon, CheckBadgeIcon, EyeIcon, TrophyIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { Container, EmptyState, Loading, MetricCard, PageHeader, RowAction, Table, Tabs, Tag } from "../../ui";
import { closingOf, countryName, destinationOf, hasDestination, kindOf, needsSponsor, stateOf, useStaffScholarship } from "../model";
import ApplicationDialog from "./ApplicationDialog";
import AwardListPanel from "./AwardListPanel";
import BeneficiariesPanel from "../cooperation/BeneficiariesPanel";
import NomineesPanel from "../cooperation/NomineesPanel";
import { BEING_MARKED, FULLY_MARKED, applicationStateOf, formatScore, fullName, useRanking } from "./model";
import { ApplicantName } from "./EligibilityFlag";

// Every call is decided by its award list. A loan's list is HEC's and goes to the DG, then the
// Minister; a sponsored scholarship's nomination list, made by HEC or the DG, sends its nominees
// straight to the sponsor, who grants them. The scholarship's owner follows up the awardees
const tabsFor = (scholarship) => [
  { value: "programmes", label: "Programmes", icon: <AcademicCapIcon className="h-4 w-4" /> },
  { value: "ranking", label: "Ranking", icon: <ChartBarIcon className="h-4 w-4" /> },
  { value: "award", label: needsSponsor(scholarship.type) ? "Nomination list" : "Award list", icon: <TrophyIcon className="h-4 w-4" /> },
  ...(scholarship.sentToDgAt && needsSponsor(scholarship.type) ? [{ value: "nominees", label: "Partner's answers", icon: <CheckBadgeIcon className="h-4 w-4" /> }] : []),
  ...(scholarship.sentToDgAt ? [{ value: "beneficiaries", label: "Awardees", icon: <UserGroupIcon className="h-4 w-4" /> }] : []),
];

const stateTag = (state) => <Tag color={applicationStateOf(state).color}>{applicationStateOf(state).label}</Tag>;
const progress = (row) => `${row.evaluationsSubmitted} / ${row.evaluationsExpected}`;

// Applications grouped by programme; counts come from the ranking, which lists every active application
function ProgrammesPanel({ scholarship }) {
  const navigate = useNavigate();
  const { data: ranking = [], isPending } = useRanking(scholarship.id);
  const open = (programme) => navigate(`${kindOf(scholarship.type).applicantsPath}/${scholarship.id}/programmes/${programme.id}`);

  const rows = scholarship.programmes.map((p) => {
    const mine = ranking.filter((r) => r.programme.id === p.id);
    const count = (states) => mine.filter((r) => states.includes(r.state)).length;
    return { ...p, applicants: mine.length, marked: count(FULLY_MARKED), inReview: count(BEING_MARKED) };
  });

  // Abroad loan programmes are study levels: the student names the university and the country
  const places = hasDestination(scholarship.type);
  const columns = [
    { key: "name", label: "Programme", render: (p) => <span className="font-medium text-slate-800">{p.name}</span> },
    ...(places
      ? [
          { key: "institutionName", label: "University" },
          { key: "country", label: "Country", accessor: (p) => countryName(p.countryCode) },
        ]
      : []),
    { key: "level", label: "Level", accessor: (p) => p.level?.name ?? "—" },
    { key: "applicants", label: "Applicants", render: (p) => <span className="font-semibold text-slate-900">{p.applicants}</span> },
    { key: "inReview", label: "Being marked" },
    { key: "marked", label: "Fully marked" },
    { key: "open", label: "", exportable: false, render: (p) => <RowAction Icon={EyeIcon} label="Applicants" onClick={() => open(p)} /> },
  ];

  return (
    <Table cardView viewKey="scholarship-programmes"
      columns={columns}
      data={rows}
      onRowClick={open}
      isLoading={isPending}
      searchable={false}
      exportName={`programmes-${scholarship.name}`}
      emptyTitle="This scholarship offers no programmes"
    />
  );
}

function RankingPanel({ scholarship, onOpen }) {
  const { data = [], isPending } = useRanking(scholarship.id);

  const columns = [
    { key: "rank", label: "Rank", accessor: (r) => r.rank ?? "—", sortValue: (r) => r.rank ?? 999 },
    { key: "applicant", label: "Applicant", accessor: (r) => fullName(r.applicant), render: (r) => <ApplicantName name={fullName(r.applicant)} meets={r.meetsEligibility} /> },
    { key: "programme", label: "Programme", accessor: (r) => r.programme.name },
    { key: "meanScore", label: "Mean score", accessor: (r) => formatScore(r.meanScore), sortValue: (r) => r.meanScore },
    // Each evaluator's total, so a mark far from the others stands out
    { key: "evaluations", label: "Evaluator totals", accessor: (r) => r.evaluations.map((e) => `${e.evaluator.name}: ${formatScore(e.weightedTotal)}`).join(" · ") || "—" },
    { key: "marked", label: "Marked", accessor: progress },
    { key: "conflicts", label: "Conflicts", accessor: (r) => r.conflictsDeclared },
    { key: "state", label: "State", accessor: (r) => applicationStateOf(r.state).label, render: (r) => stateTag(r.state) },
    { key: "open", label: "", exportable: false, render: (r) => <RowAction Icon={EyeIcon} label="View" onClick={() => onOpen(r.applicationId)} /> },
  ];

  return (
    <div className="flex flex-col gap-3">
      <Table cardView viewKey="scholarship-ranking" columns={columns} data={data} isLoading={isPending} searchable exportName={`ranking-${scholarship.name}`} emptyTitle="No applications yet" />
    </div>
  );
}

// One scholarship under review: its programmes (each opening its applicants), the ranking and the award list
export default function ScholarshipReviewPage() {
  const { scholarshipId } = useParams();
  const [tab, setTab] = useState("programmes");
  const [focusId, setFocusId] = useState(null); // a granted nominee whose progress is recorded next
  const recordProgress = (applicationId) => {
    setFocusId(applicationId);
    setTab("beneficiaries");
  };
  const clearFocus = useCallback(() => setFocusId(null), []);
  const [openId, setOpenId] = useState(null);
  const { data, isPending, isError, refetch } = useStaffScholarship(scholarshipId);
  const scholarship = data?.data;

  if (isPending) return <Loading label="Loading the scholarship…" />;
  if (isError) return <EmptyState title="Could not load this scholarship" description="It may have been removed, or you may not have access." onRefresh={refetch} />;

  const counts = scholarship.applicationCounts ?? {};
  const state = stateOf(scholarship.state);
  const total = (states) => states.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
  const reviewed = total(FULLY_MARKED);
  const inReview = total(BEING_MARKED);

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={kindOf(scholarship.type).applicantsPath} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> All {kindOf(scholarship.type).many}
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {scholarship.name}
            <Tag color={state.color}>{state.label}</Tag>
          </span>
        }
        subtitle={`${destinationOf(scholarship)} · ${scholarship.sponsor?.name ?? "HEC"} · ${closingOf(scholarship)}`}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard title="Submitted" value={scholarship.submittedApplications} />
        <MetricCard title="Being marked" value={inReview} />
        <MetricCard title="Fully marked" value={reviewed} />
        <MetricCard title="Places" value={scholarship.awardQuota ?? "—"} />
      </div>
      <Tabs tabs={tabsFor(scholarship)} value={tab} onChange={setTab} />
      {tab === "programmes" && <ProgrammesPanel scholarship={scholarship} />}
      {tab === "ranking" && <RankingPanel scholarship={scholarship} onOpen={setOpenId} />}
      {tab === "award" && <AwardListPanel scholarship={scholarship} />}
      {tab === "nominees" && <NomineesPanel scholarship={scholarship} basePath="admin" onRecordProgress={recordProgress} />}
      {tab === "beneficiaries" && <BeneficiariesPanel scholarship={scholarship} canChange basePath="admin" focusId={focusId} onFocused={clearFocus} />}
      <ApplicationDialog key={openId} applicationId={openId} programmes={scholarship.programmes} sponsored={needsSponsor(scholarship.type)} onClose={() => setOpenId(null)} />
    </Container>
  );
}
